import type { KspPublishedSnapshot } from '@entities/ksp/types';
import { decimal, label, projectDate } from '@entities/ksp/format';
import { Card, Grid, Muted, Notice, Small, Table, TableBox } from './parts';

function SourceCountNotice({
  shown,
  data,
}: {
  shown: number;
  data: Pick<
    KspPublishedSnapshot,
    | 'source_ref_count'
    | 'source_ref_count_kind'
    | 'ledger_record_count'
    | 'schedule_source_ref_count'
  >;
}) {
  if (data.source_ref_count_kind === 'distinct_accepted_sources')
    return (
      <Small>
        Источников принятого факта: {data.source_ref_count ?? '—'} · записей реестра:{' '}
        {data.ledger_record_count ?? '—'} · ссылок графика: {data.schedule_source_ref_count ?? '—'}.
        Показано ссылок: {shown}; полный состав сохранён в снимке и его экспорте.
      </Small>
    );
  return (
    <Small>
      Показано {shown} из {data.source_ref_count ?? 'неизвестного числа'} источников; полный состав
      сохранён в снимке и его экспорте.
    </Small>
  );
}

export function KspSummary({ data }: { data: KspPublishedSnapshot | null }) {
  if (!data)
    return (
      <Notice>
        Опубликованного расчёта ещё нет. Проверьте исходные данные, рассчитайте график и опубликуйте
        полный снимок.
      </Notice>
    );
  return (
    <Card>
      <h3>Опубликованный план-факт</h3>
      <Muted>
        Дата состояния: {projectDate(data.data_date, data.timezone)} · {data.timezone} ·{' '}
        {label(data.coverage)} · расчёт {projectDate(data.calculated_at, data.timezone)}
        {data.calendars_timezone_database_version &&
          ` · база часовых поясов календарей ${data.calendars_timezone_database_version}`}
      </Muted>
      <Grid>
        {data.kpis.map((kpi) => (
          <Card key={kpi.code}>
            <strong>{label(kpi.code.replace('ksp.', ''))}</strong>
            <h3>{decimal(kpi.fact_pct, ' %')}</h3>
            <Muted>
              План {decimal(kpi.plan_pct, ' %')} · отклонение {decimal(kpi.delta_pp, ' п.п.')}
            </Muted>
            <Small>
              {label(kpi.scope_kind)} · {label(kpi.method)} · {label(kpi.status)}
            </Small>
            <Small>
              {(kpi.reasons ?? (kpi.reason ? [kpi.reason] : [])).map(label).join(' · ')}
            </Small>
            {kpi.source_refs.map((ref) => (
              <Small key={ref}>{ref}</Small>
            ))}
            {kpi.source_refs_truncated && (
              <SourceCountNotice shown={kpi.source_refs.length} data={kpi} />
            )}
          </Card>
        ))}
      </Grid>
      <TableBox>
        <Table>
          <tbody>
            <tr>
              <th>Договорное окончание</th>
              <td>{projectDate(data.contract_finish, data.timezone)}</td>
              <th>Прогноз</th>
              <td>{projectDate(data.forecast_finish, data.timezone)}</td>
            </tr>
            <tr>
              <th>Отклонение, календарные дни</th>
              <td>{data.finish_deviation_calendar_days ?? '—'}</td>
              <th>Открытые предупреждения в снимке</th>
              <td>{data.open_milestone_alert_count}</td>
            </tr>
          </tbody>
        </Table>
      </TableBox>
      {data.quality.reasons.length > 0 && (
        <Notice>{data.quality.reasons.map(label).join('\n')}</Notice>
      )}
      {data.source_refs_truncated && (
        <SourceCountNotice shown={data.source_refs?.length ?? 0} data={data} />
      )}
      <Small>
        Снимок {data.snapshot_id} · версия входов {data.input_version} ·{' '}
        {data.quality.stale ? 'Данные устарели' : 'Согласованный срез'}
        {data.operational_as_of
          ? ` · оперативный срез ${projectDate(data.operational_as_of, data.timezone)}`
          : ''}
      </Small>
    </Card>
  );
}
